import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../api/client.js';
import clsx from 'clsx';
import ExportButton from '../components/ExportButton.jsx';
import FollowupEditor from '../components/FollowupEditor.jsx';
import VoiceFollowupCapture from '../components/VoiceFollowupCapture.jsx';
import LogOutreachModal from '../components/LogOutreachModal.jsx';
import { useAuth } from '../auth/AuthContext.jsx';

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

const ADMIN_ROLES = ['superadmin', 'dealer_admin', 'branch_admin'];

export default function Followups() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [view, setView] = useState('list'); // 'list' | 'queue'
  const [filter, setFilter] = useState('pending');
  const [scope, setScope] = useState('all'); // 'all' | 'customer' | 'general'
  const [filterUserId, setFilterUserId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [editorInitial, setEditorInitial] = useState(null);
  const [hideSnooze] = useState(() => localStorage.getItem('utsales:hide_snooze_btn') === 'true');
  const [snoozeOpenId, setSnoozeOpenId] = useState(null);
  const [loggingOutreachFor, setLoggingOutreachFor] = useState(null);
  const qc = useQueryClient();

  const invalidateFollowups = () => {
    qc.invalidateQueries({ queryKey: ['followups'] });
    qc.invalidateQueries({ queryKey: ['followups-queue'] });
  };

  const queue = useQuery({
    queryKey: ['followups-queue', filterUserId],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterUserId) params.set('user_id', filterUserId);
      return api.get(`/followups/queue?${params}`).then(r => r.data.data);
    },
    enabled: view === 'queue',
  });

  // Preset snooze durations (label + ms to push the due date out by).
  const SNOOZE_PRESETS = [
    { label: '1 hour', ms: 60 * 60 * 1000 },
    { label: '3 hours', ms: 3 * 60 * 60 * 1000 },
    { label: 'Tomorrow', ms: 24 * 60 * 60 * 1000 },
    { label: '3 days', ms: 3 * 24 * 60 * 60 * 1000 },
    { label: '1 week', ms: 7 * 24 * 60 * 60 * 1000 },
  ];
  const list = useQuery({
    queryKey: ['followups', filter, filterUserId],
    queryFn: () => {
      const params = new URLSearchParams({ status: filter });
      if (filterUserId) params.set('user_id', filterUserId);
      return api.get(`/followups?${params}`).then(r => r.data.data);
    },
  });

  // Derive unique reps from the unfiltered list for the admin rep selector
  const allList = useQuery({
    queryKey: ['followups', filter, null],
    queryFn: () => api.get(`/followups?status=${filter}`).then(r => r.data.data),
    enabled: isAdmin,
  });
  const reps = useMemo(() => {
    if (!isAdmin || !allList.data) return [];
    const seen = new Set();
    const out = [];
    for (const f of allList.data) {
      if (f.user_id && !seen.has(f.user_id)) {
        seen.add(f.user_id);
        out.push({ id: f.user_id, name: f.user_name || `Rep #${f.user_id}` });
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [isAdmin, allList.data]);

  const filtered = (list.data || []).filter(f => {
    if (scope === 'customer') return f.customer_id != null;
    if (scope === 'general') return f.customer_id == null;
    return true;
  });

  const todayISO = new Date().toISOString().slice(0, 10);
  const dueToday = (list.data || []).filter(f =>
    f.status === 'pending' && String(f.due_date).slice(0, 10) === todayISO
  );

  const update = useMutation({
    mutationFn: ({ id, ...body }) => api.put(`/followups/${id}`, body).then(r => r.data.data),
    onSuccess: () => {
      setSnoozeOpenId(null);
      invalidateFollowups();
    },
  });

  // Never leave a snooze dropdown bound to an item that's no longer in the
  // visible list (filter/scope change or refetch) — stale state could otherwise
  // surface the menu against the wrong row.
  useEffect(() => {
    if (snoozeOpenId != null && !filtered.some(f => f.id === snoozeOpenId)) {
      setSnoozeOpenId(null);
    }
  }, [filtered, snoozeOpenId]);

  const snooze = (f, ms) => {
    const due_date = new Date(Date.now() + ms).toISOString();
    update.mutate({ id: f.id, status: 'snoozed', due_date });
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <div className="section-eyebrow">Tasks</div>
          <h1 className="text-2xl">Follow-ups</h1>
          <div className="rule-crimson" />
        </div>
        <div className="flex gap-2 items-center">
          {isAdmin && reps.length > 0 && (
            <select
              className="input text-sm"
              value={filterUserId ?? ''}
              onChange={e => setFilterUserId(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">All reps</option>
              {reps.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
          <ExportButton path="/export/followups.csv" />
          <button className="btn-ghost" onClick={() => setVoiceOpen(true)}>🎙 Voice</button>
          <button className="btn-primary" onClick={() => setCreating(true)}>+ New</button>
        </div>
      </header>

      <div className="flex gap-2">
        {[{ id: 'list', label: 'List' }, { id: 'queue', label: '📋 My Queue' }].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={clsx('btn px-3 py-1 text-sm font-medium',
              view === v.id ? 'bg-navy text-white' : 'bg-panel text-navy')}>
            {v.label}
          </button>
        ))}
      </div>

      {view === 'queue' && (
        <MyQueueView
          rows={queue.data}
          isLoading={queue.isLoading}
          isError={queue.isError}
          onRetry={() => queue.refetch()}
          onDone={(id) => update.mutate({ id, status: 'done' })}
          onLogOutreach={(row) => setLoggingOutreachFor(row)}
        />
      )}

      {view === 'list' && dueToday.length > 0 && filter !== 'done' && (
        <div className="rounded border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="font-semibold mb-1">
            {dueToday.length === 1 ? '1 follow-up is due today' : `${dueToday.length} follow-ups are due today`}
          </div>
          <div className="space-y-1">
            {dueToday.map(f => (
              <Link key={f.id} to={`/followups/${f.id}`}
                className="flex items-center gap-2 text-amber-800 hover:text-amber-900 hover:underline">
                <span className="text-xs">·</span>
                <span className="font-medium">{f.title}</span>
                {(f.customer_name || f.customer_name_hint || f.source_customer_name) && (
                  <span className="text-xs text-amber-600">— {f.customer_name || f.customer_name_hint || f.source_customer_name}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {view === 'list' && (
      <div className="flex gap-2 flex-wrap">
        {['pending', 'done', 'missed', 'all'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={clsx('btn px-3 py-1 text-sm capitalize',
              filter === s ? 'bg-navy text-white' : 'bg-panel text-navy')}>
            {s}
          </button>
        ))}
        <span className="text-muted self-center mx-2">·</span>
        {[
          { id: 'all', label: 'All' },
          { id: 'customer', label: 'Customer' },
          { id: 'general', label: 'General task' },
        ].map(s => (
          <button key={s.id} onClick={() => setScope(s.id)}
            className={clsx('btn px-3 py-1 text-sm',
              scope === s.id ? 'bg-navy text-white' : 'bg-panel text-navy')}>
            {s.label}
          </button>
        ))}
      </div>
      )}

      {view === 'list' && list.isLoading && (
        <div className="card p-4 text-muted text-sm">Loading follow-ups…</div>
      )}
      {view === 'list' && list.isError && (
        <div className="card p-4 text-sm text-crimson flex items-center justify-between gap-2">
          <span>Couldn't load follow-ups.</span>
          <button className="btn-ghost text-xs" onClick={() => list.refetch()}>Retry</button>
        </div>
      )}

      {view === 'list' && !list.isLoading && !list.isError && (
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="card p-4 text-muted text-sm">No follow-ups.</div>
        )}
        {filtered.map(f => {
          const isOverdue = f.status === 'pending' && new Date(f.due_date) < new Date();
          return (
          <div key={f.id} className={clsx('card p-3 border-l-4',
            (isOverdue || f.status === 'missed') ? 'border-crimson bg-red-50/30'
            : f.urgency === 'high' ? 'border-crimson'
            : f.urgency === 'medium' ? 'border-gold'
            : 'border-navy-200')}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-navy">{f.title}</span>
                  {isOverdue && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-crimson bg-red-100 px-1.5 py-0.5 rounded">
                      Overdue
                    </span>
                  )}
                  {f.status === 'missed' && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-crimson bg-red-100 px-1.5 py-0.5 rounded">
                      Missed
                    </span>
                  )}
                </div>
                {f.customer_name
                  ? <div className="text-xs text-muted">{f.customer_name} <span className="text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">🔗 linked</span></div>
                  : (f.customer_name_hint || f.source_customer_name)
                    ? <div className="text-xs text-muted">{f.customer_name_hint || f.source_customer_name} <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">unlinked</span></div>
                    : <div className="text-xs text-muted italic">General task</div>}
                {f.description && <div className="text-sm mt-1">{f.description}</div>}
                <div className={clsx('text-xs mt-1', isOverdue ? 'text-crimson font-medium' : 'text-muted')}>
                  Due {new Date(f.due_date).toLocaleString()} · {f.urgency} · from {f.source}
                  {isAdmin && f.user_name && <span className="ml-1">· {f.user_name}</span>}
                </div>
              </div>
              {(f.status === 'pending' || f.status === 'missed') && (
                <div className="flex flex-col gap-1">
                  <button className="btn-primary text-xs px-2 py-1"
                          onClick={() => update.mutate({ id: f.id, status: 'done' })}>Done</button>
                  {f.customer_id && (
                    <button
                      className="btn-ghost text-xs px-2 py-1"
                      onClick={() => setLoggingOutreachFor(f)}
                    >
                      📞 Log outreach
                    </button>
                  )}
                  {!hideSnooze && (
                    <div className="relative">
                      <button className="btn-ghost text-xs px-2 py-1 w-full"
                              onClick={() => setSnoozeOpenId(snoozeOpenId === f.id ? null : f.id)}>
                        Snooze ▾
                      </button>
                      {snoozeOpenId === f.id && (
                        <div className="absolute right-0 z-10 mt-1 card p-1 flex flex-col min-w-[8rem]">
                          {SNOOZE_PRESETS.map(p => (
                            <button key={p.label}
                                    className="btn-ghost text-xs px-2 py-1 text-left"
                                    onClick={() => snooze(f, p.ms)}>
                              {p.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {f.status !== 'pending' && f.status !== 'missed' && (
                <span className="text-xs uppercase tracking-wider text-muted">{f.status}</span>
              )}
            </div>
          </div>
          );
        })}
      </div>
      )}

      {creating && (
        <FollowupEditor
          initial={editorInitial || {}}
          onClose={() => { setCreating(false); setEditorInitial(null); }}
          onSaved={() => { setCreating(false); setEditorInitial(null); }}
        />
      )}

      {loggingOutreachFor && (
        <LogOutreachModal
          customerId={loggingOutreachFor.customer_id}
          customerName={loggingOutreachFor.customer_name}
          followUpId={loggingOutreachFor.id}
          defaultContactName={loggingOutreachFor.contact_name}
          onClose={() => setLoggingOutreachFor(null)}
          onLogged={invalidateFollowups}
        />
      )}

      {voiceOpen && (
        <VoiceFollowupCapture
          onClose={() => setVoiceOpen(false)}
          onSaved={() => { setVoiceOpen(false); invalidateFollowups(); }}
          onEdit={(initial) => {
            setVoiceOpen(false);
            setEditorInitial(initial);
            setCreating(true);
          }}
        />
      )}
    </div>
  );
}

// CR1 — "My Queue": one row per pending follow-up, enriched with the
// account's primary contact/role and last interaction so a rep can work
// straight down the list without opening a separate page per account.
function MyQueueView({ rows, isLoading, isError, onRetry, onDone, onLogOutreach }) {
  if (isLoading) return <div className="card p-4 text-muted text-sm">Loading your queue…</div>;
  if (isError) {
    return (
      <div className="card p-4 text-sm text-crimson flex items-center justify-between gap-2">
        <span>Couldn't load your queue.</span>
        <button className="btn-ghost text-xs" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (!rows?.length) return <div className="card p-4 text-muted text-sm">Nothing in your queue.</div>;

  return (
    <div className="space-y-2">
      {rows.map(row => {
        const isOverdue = new Date(row.due_date) < new Date();
        const lastDays = daysSince(row.last_interaction_at);
        return (
          <div key={row.id} className={clsx('card p-3 border-l-4 flex items-start justify-between gap-3',
            isOverdue ? 'border-crimson bg-red-50/30'
            : row.urgency === 'high' ? 'border-crimson'
            : row.urgency === 'medium' ? 'border-gold'
            : 'border-navy-200')}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {row.customer_id
                  ? <Link to={`/customers/${row.customer_id}`} className="font-medium text-navy hover:text-crimson truncate">
                      {row.customer_name}
                    </Link>
                  : <span className="font-medium text-navy italic">{row.customer_name_hint || 'General task'}</span>}
                {row.contact_name && (
                  <span className="text-xs text-muted">
                    👤 {row.contact_name}{row.contact_role && ` · ${row.contact_role}`}
                  </span>
                )}
              </div>
              <div className="text-xs text-muted mt-0.5">
                Last interaction: {lastDays != null ? `${lastDays}d ago` : 'never'}
              </div>
              <Link to={`/followups/${row.id}`} className="block text-sm mt-1 hover:underline">
                <span className={isOverdue ? 'text-crimson font-medium' : 'text-navy'}>{row.title}</span>
                <span className="text-muted"> · due {new Date(row.due_date).toLocaleDateString()} · {row.urgency}</span>
              </Link>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button className="btn-primary text-xs px-2 py-1" onClick={() => onDone(row.id)}>Done</button>
              {row.customer_id && (
                <button className="btn-ghost text-xs px-2 py-1" onClick={() => onLogOutreach(row)}>
                  📞 Log outreach
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
